import { type ReactNode, useMemo, type JSX, type ContextType } from "react";

import { useLingui } from "@lingui/react";
import { LocalesContext } from "@mobsuccess-devops/react-ui/Locales";

import { useI18n } from "./i18n";

type ReactUILocalesProps = {
  children: ReactNode;
};

export function ReactUILocales({ children }: ReactUILocalesProps): JSX.Element {
  const { locale } = useI18n();
  const { i18n } = useLingui();
  const dictionay = useMemo<ContextType<typeof LocalesContext>>(
    () => ({
      locale: locale,
      field: {
        optional: i18n._("src.public.react-ui.field.optional"),
        charCount(count, limit) {
          return i18n._("src.public.react-ui.field.char-count", {
            count,
            limit,
          });
        },
      },
      general: {
        save: i18n._("src.public.react-ui.general.save"),
        reset: i18n._("src.public.react-ui.general.reset"),
        saveAndClose: i18n._("src.public.react-ui.general.save-and-close"),
      },
      confirmSubmit: {
        title: i18n._("src.public.react-ui.general.confirm-submit.title"),
        confirm: i18n._("src.public.react-ui.general.confirm-submit.confirm"),
        cancel: i18n._("src.public.react-ui.general.confirm-submit.cancel"),
      },
      confirmReset: {
        title: i18n._("src.public.react-ui.general.confirm-reset.title"),
        description: i18n._(
          "src.public.react-ui.general.confirm-reset.description",
        ),
        confirm: i18n._("src.public.react-ui.general.confirm-reset.confirm"),
        cancel: i18n._("src.public.react-ui.general.confirm-reset.cancel"),
      },
      errorPage: {
        title: i18n._("src.public.react-ui.error-page.title"),
        description: i18n._("src.public.react-ui.error-page.description"),
        contactSupport: i18n._(
          "src.public.react-ui.error-page.contact-support",
        ),
        goBack: i18n._("src.public.react-ui.error-page.go-back"),
        goHome: i18n._("src.public.react-ui.error-page.go-home"),
      },
      notFound: {
        title: i18n._("src.public.react-ui.not-found-error-page.title"),
        description: i18n._(
          "src.public.react-ui.not-found-error-page.description",
        ),
        clearFilters: i18n._(
          "src.public.react-ui.not-found-error-page.clear-filters",
        ),
      },
      notAllowed: {
        title: i18n._("src.public.react-ui.not-allowed.title"),
        description: i18n._("src.public.react-ui.not-allowed.description"),
        requestId: (id: string) =>
          i18n._("src.public.react-ui.not-allowed.request-id", { id }),
        goBack: i18n._("src.public.react-ui.not-allowed.go-back"),
        goHome: i18n._("src.public.react-ui.not-allowed.go-home"),
      },
      select: {
        selectAll: i18n._("src.public.react-ui.select.select-all"),
        removeAll: i18n._("src.public.react-ui.select.remove-all"),
        copy: i18n._("src.public.react-ui.select.copy"),
        copyTooltip: i18n._("src.public.react-ui.select.copy-tooltip"),
        copySuccess: i18n._("src.public.react-ui.select.copy-success"),
        paste: i18n._("src.public.react-ui.select.paste"),
        pasteTooltip: i18n._("src.public.react-ui.select.paste-tooltip"),
        pasteErrorTitle: i18n._("src.public.react-ui.select.paste-error-title"),
        pasteErrorDescription: i18n._(
          "src.public.react-ui.select.paste-error-description",
        ),
      },
      autocomplete: {
        nextPage: i18n._("src.public.react-ui.autocomplete.next-page"),
      },
      commandMenu: {
        placeholder: i18n._("src.public.react-ui.command-menu.placeholder"),
        empty: i18n._("src.public.react-ui.command-menu.empty"),
        navigateHint: i18n._("src.public.react-ui.command-menu.navigate-hint"),
        selectHint: i18n._("src.public.react-ui.command-menu.select-hint"),
        closeHint: i18n._("src.public.react-ui.command-menu.close-hint"),
        toggleHint: i18n._("src.public.react-ui.command-menu.toggle-hint"),
      },
      accountDropdown: {
        searchPlaceholder: i18n._(
          "src.public.react-ui.account-dropdown.search-placeholder",
        ),
      },
      headerAccountPicker: {
        searchPlaceholder: i18n._(
          "src.public.react-ui.header-account-picker.search-placeholder",
        ),
        selectAccount: i18n._(
          "src.public.react-ui.header-account-picker.select-account",
        ),
      },
      pagination: {
        pageSizeLabel: i18n._("src.public.react-ui.pagination.page-size-label"),
        pageSizeOptions: (size: number) =>
          i18n._("src.public.react-ui.pagination.page-size-options", { size }),
        firstPage: i18n._("src.public.react-ui.pagination.first-page"),
        previousPage: i18n._("src.public.react-ui.pagination.previous-page"),
        nextPage: i18n._("src.public.react-ui.pagination.next-page"),
        lastPage: i18n._("src.public.react-ui.pagination.last-page"),
      },
      filters: {
        searchPlaceholder: i18n._(
          "src.public.react-ui.filters.search-placeholder",
        ),
      },
      table: {
        arrangeColumns: i18n._("src.public.react-ui.table.arrange-columns"),
        expandRow: i18n._("src.public.react-ui.table.expand-row"),
        collapseRow: i18n._("src.public.react-ui.table.collapse-row"),
        expandAllRows: i18n._("src.public.react-ui.table.expand-all-rows"),
        collapseAllRows: i18n._("src.public.react-ui.table.collapse-all-rows"),
      },
      dateRangePicker: {
        reset: i18n._("src.public.react-ui.date-range-picker.reset"),
        from: i18n._("src.public.react-ui.date-range-picker.from"),
        to: i18n._("src.public.react-ui.date-range-picker.to"),
        rangePlaceholder: i18n._(
          "src.public.react-ui.date-range-picker.range-placeholder",
        ),
        thisWeek: i18n._("src.public.react-ui.date-range-picker.this-week"),
        lastWeek: i18n._("src.public.react-ui.date-range-picker.last-week"),
        thisMonth: i18n._("src.public.react-ui.date-range-picker.this-month"),
        lastMonth: i18n._("src.public.react-ui.date-range-picker.last-month"),
        thisQuarter: i18n._(
          "src.public.react-ui.date-range-picker.this-quarter",
        ),
        lastQuarter: i18n._(
          "src.public.react-ui.date-range-picker.last-quarter",
        ),
        thisYear: i18n._("src.public.react-ui.date-range-picker.this-year"),
        lastYear: i18n._("src.public.react-ui.date-range-picker.last-year"),
        last3Days: i18n._("src.public.react-ui.date-range-picker.last-days", {
          days: 3,
        }),
        last7Days: i18n._("src.public.react-ui.date-range-picker.last-days", {
          days: 7,
        }),
        last14Days: i18n._("src.public.react-ui.date-range-picker.last-days", {
          days: 14,
        }),
        last30Days: i18n._("src.public.react-ui.date-range-picker.last-days", {
          days: 30,
        }),
        last90Days: i18n._("src.public.react-ui.date-range-picker.last-days", {
          days: 90,
        }),
        today: i18n._("src.public.react-ui.date-range-picker.today"),
        yesterday: i18n._("src.public.react-ui.date-range-picker.yesterday"),
      },
      dateTimePicker: {
        ok: i18n._("src.public.react-ui.date-time-picker.ok"),
      },
      sidebar: {
        languageSelector: {
          title: i18n._(
            "src.public.react-ui.sidebar.language-selector-dialog.title",
          ),
          description: i18n._(
            "src.public.react-ui.sidebar.language-selector-dialog.description",
          ),
          cancel: i18n._(
            "src.public.react-ui.sidebar.language-selector-dialog.cancel",
          ),
          confirm: i18n._(
            "src.public.react-ui.sidebar.language-selector-dialog.confirm",
          ),
          errorTitle: i18n._(
            "src.public.react-ui.sidebar.language-selector-dialog.error-title",
          ),
          errorDescription: i18n._(
            "src.public.react-ui.sidebar.language-selector-dialog.error-description",
          ),
        },
        logout: i18n._("src.public.react-ui.sidebar.logout"),
        admin: i18n._("src.public.react-ui.sidebar.admin"),
        editTranslations: i18n._(
          "src.public.react-ui.sidebar.edit-translations",
        ),
        openMenu: i18n._("src.public.react-ui.sidebar.open-menu"),
      },
      moby: {
        title: i18n._("src.public.react-ui.moby.title"),
        send: i18n._("src.public.react-ui.moby.send"),
        askAQuestion: i18n._("src.public.react-ui.moby.ask-a-question"),
        copyMessage: i18n._("src.public.react-ui.moby.copy-message"),
        copiedMessage: i18n._("src.public.react-ui.moby.copied-message"),
        resetRequest: i18n._("src.public.react-ui.moby.reset-request"),
        resetRequestDescription: i18n._(
          "src.public.react-ui.moby.reset-request-description",
        ),
        cancel: i18n._("src.public.react-ui.moby.cancel"),
        reset: i18n._("src.public.react-ui.moby.reset"),
        happyWithThisConv: i18n._(
          "src.public.react-ui.moby.happy-with-this-conv",
        ),
        description: i18n._("src.public.react-ui.moby.description"),
        openChat: i18n._("src.public.react-ui.moby.open-chat"),
        downloadFile: i18n._("src.public.react-ui.moby.download-file"),
        downloadingFile: i18n._("src.public.react-ui.moby.downloading-file"),
        downloadFileError: i18n._(
          "src.public.react-ui.moby.download-file-error",
        ),
      },
      audioPlayer: {
        play: i18n._("src.public.react-ui.audio-player.play"),
        pause: i18n._("src.public.react-ui.audio-player.pause"),
        error: i18n._("src.public.react-ui.audio-player.error"),
        loading: i18n._("src.public.react-ui.audio-player.loading"),
      },
      dataEditor: {
        upload: {
          error: i18n._("src.public.react-ui.data-editor.upload.error"),
        },
        views: {
          table: i18n._("src.public.react-ui.data-editor.views.table"),
          csv: i18n._("src.public.react-ui.data-editor.views.csv"),
        },
        csv: {
          apply: i18n._("src.public.react-ui.data-editor.csv.apply"),
          revert: i18n._("src.public.react-ui.data-editor.csv.revert"),
          format: i18n._("src.public.react-ui.data-editor.csv.format"),
          upToDate: i18n._("src.public.react-ui.data-editor.csv.up-to-date"),
          unsavedChanges: i18n._(
            "src.public.react-ui.data-editor.csv.unsaved-changes",
          ),
        },
        records(count: number) {
          return i18n._("src.public.react-ui.data-editor.records", { count });
        },
      },
      fileUpload: {
        rejected: {
          title(name: string) {
            return i18n._("src.public.react-ui.file-upload.rejected.title", {
              name,
            });
          },
          reason(reasons: string[]) {
            return i18n._("src.public.react-ui.file-upload.rejected.reason", {
              reasons: reasons.join(", "),
            });
          },
        },
      },
      advertiserPicker: {
        noResults: i18n._("src.public.react-ui.advertiser-picker.no-results"),
        placeholder: i18n._(
          "src.public.react-ui.advertiser-picker.placeholder",
        ),
        noSelection: i18n._(
          "src.public.react-ui.advertiser-picker.no-selection",
        ),
        favoritesFilter: i18n._(
          "src.public.react-ui.advertiser-picker.favorites-filter",
        ),
        favoritesSection: i18n._(
          "src.public.react-ui.advertiser-picker.favorites-section",
        ),
        addToFavorites: i18n._(
          "src.public.react-ui.advertiser-picker.add-to-favorites",
        ),
        removeFromFavorites: i18n._(
          "src.public.react-ui.advertiser-picker.remove-from-favorites",
        ),
        navigateHint: i18n._(
          "src.public.react-ui.advertiser-picker.navigate-hint",
        ),
        selectHint: i18n._("src.public.react-ui.advertiser-picker.select-hint"),
        closeHint: i18n._("src.public.react-ui.advertiser-picker.close-hint"),
        toggleHint: i18n._("src.public.react-ui.advertiser-picker.toggle-hint"),
        toggleTooltip(shortcut: string) {
          return i18n._(
            "src.public.react-ui.advertiser-picker.toggle-tooltip",
            { shortcut },
          );
        },
        accountInfo(accountId: string, clientId: string) {
          return i18n._("src.public.react-ui.advertiser-picker.account-info", {
            accountId,
            clientId,
          });
        },
      },
      calendar: {
        today: i18n._("src.public.react-ui.calendar.today"),
        month: i18n._("src.public.react-ui.calendar.month"),
        twoMonths: i18n._("src.public.react-ui.calendar.two-months"),
        year: i18n._("src.public.react-ui.calendar.year"),
        weekLetter: i18n._("src.public.react-ui.calendar.week-letter"),
        others: i18n._("src.public.react-ui.calendar.others"),
        nextMonth: i18n._("src.public.react-ui.calendar.next-month"),
        previousMonth: i18n._("src.public.react-ui.calendar.previous-month"),
        toggleAllItems: i18n._("src.public.react-ui.calendar.toggle-all-items"),
      },
      chat: {
        labels: {
          favoriteButton: i18n._(
            "src.public.react-ui.chat.labels.favorite-button",
          ),
          selectedTags: i18n._("src.public.react-ui.chat.labels.selected-tags"),
          availableTags: i18n._(
            "src.public.react-ui.chat.labels.available-tags",
          ),
          backToConversation: i18n._(
            "src.public.react-ui.chat.labels.back-to-conversation",
          ),
          favoriteCount(count: number) {
            return i18n._("src.public.react-ui.chat.labels.favorite-count", {
              count,
            });
          },
        },
        placeholders: {
          search: i18n._("src.public.react-ui.chat.placeholders.search"),
        },
        send: i18n._("src.public.react-ui.chat.send-button"),
        loading: i18n._("src.public.react-ui.chat.loading"),
        emptyTitle: i18n._("src.public.react-ui.chat.empty-title"),
        emptySubtitle: i18n._("src.public.react-ui.chat.empty-subtitle"),
      },
    }),
    [locale, i18n],
  );

  return <LocalesContext value={dictionay}>{children}</LocalesContext>;
}
